import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getFactoryIdentityStatus, refreshFactoryIdentityStatus, startFactoryIdentity,
  navigateToAlipayIdentity, type FactoryIdentityStatus,
} from '../../lib/factory-identity-api';
import { factoryError } from './FactoryPrimitives';
import { detectPaymentEnvironment } from '../../lib/payment-methods';

export interface FactoryIdentityController {
  status: FactoryIdentityStatus | null;
  working: boolean;
  error: string;
  certifyUrl?: string | null;
  start: (name: string, certNo: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useFactoryIdentity(expertId: string, beforeStart: () => Promise<unknown>): FactoryIdentityController {
  const [status, setStatus] = useState<FactoryIdentityStatus | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  // A signed authentication URL is held only in component memory, never browser storage.
  const [certifyUrl, setCertifyUrl] = useState<string | null>(null);
  const active = useRef(true);
  const requestId = useRef(0);
  const starting = useRef(false);
  const load = useCallback(async (query: boolean) => {
    if (starting.current) return;
    const id = ++requestId.current;
    setWorking(true);
    try {
      const next = await (query ? refreshFactoryIdentityStatus() : getFactoryIdentityStatus());
      if (active.current && id === requestId.current) {
        setStatus(next); setError('');
        if (next.status === 'verified' || next.status === 'expired') setCertifyUrl(null);
      }
    } catch (issue) {
      if (active.current && id === requestId.current) setError(factoryError(issue));
    } finally {
      if (active.current && id === requestId.current) setWorking(false);
    }
  }, []);
  const refresh = useCallback(() => load(true), [load]);
  useEffect(() => {
    active.current = true;
    void load(false);
    const focused = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', focused);
    document.addEventListener('visibilitychange', focused);
    return () => {
      active.current = false; requestId.current++;
      window.removeEventListener('focus', focused);
      document.removeEventListener('visibilitychange', focused);
    };
  }, [load, refresh]);
  useEffect(() => {
    if (status?.status !== 'pending' && !certifyUrl) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [status?.status, certifyUrl, refresh]);

  const start = async (name: string, certNo: string) => {
    if (starting.current) return;
    starting.current = true;
    const id = ++requestId.current;
    setWorking(true); setError('');
    try {
      // Flush the draft before navigating away from the editor.
      await beforeStart();
      if (!active.current) return;
      const result = await startFactoryIdentity(expertId, name, certNo);
      if (!active.current || id !== requestId.current) return;
      setStatus(result.verification);
      if (result.certify_url) {
        const environment = detectPaymentEnvironment();
        if (environment.isMobile || environment.isAlipay) navigateToAlipayIdentity(result.certify_url);
        else setCertifyUrl(result.certify_url);
      } else setCertifyUrl(null);
    } catch (issue) {
      if (active.current && id === requestId.current) setError(factoryError(issue));
    } finally {
      starting.current = false;
      if (active.current && id === requestId.current) setWorking(false);
    }
  };
  return { status, working, error, start, refresh, certifyUrl };
}
